import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import pusherService from '../services/pusher';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const initAuth = async () => {
            const token = localStorage.getItem('auth_token');
            const cachedUser = localStorage.getItem('auth_user');

            if (cachedUser) {
                try {
                    const userData = JSON.parse(cachedUser);
                    setUser(userData);
                    await setupPusherSubscriptions(userData);
                } catch {}
            }

            if (token) {
                try {
                    api.setToken(token);
                    const response = await api.getProfile();
                    setUser(response.data.user);
                    localStorage.setItem('auth_user', JSON.stringify(response.data.user));
                    await setupPusherSubscriptions(response.data.user);
                } catch (error) {
                    console.error('Auth initialization failed:', error);
                    api.removeToken();
                    localStorage.removeItem('auth_user');
                    setUser(null);
                    await pusherService.clearAllSubscriptions();
                }
            }
            setLoading(false);
        };

        initAuth();
    }, []);

    const setupPusherSubscriptions = async (userData) => {
        try {
            await pusherService.clearAllSubscriptions();
            
            // Subscribe to user-specific notifications
            await pusherService.subscribeToUser(userData.id);
            
            // Subscribe to role-specific notifications
            if (userData.user_type === 'vendor') {
                if (userData.vendor?.id) {
                    await pusherService.subscribeToVendor(userData.vendor.id);
                    console.log('Subscribed to vendor channel:', userData.vendor.id);
                } else {
                    console.warn('Vendor user but no vendor ID found. User may need to complete vendor profile setup.');
                }
            }
            
            console.log('Pusher subscriptions set up for user:', userData.id);
        } catch (error) {
            console.error('Failed to setup Pusher subscriptions:', error);
        }
    };

    const login = async (email, password) => {
        try {
            const response = await api.login(email, password);
            const { user, token } = response.data;
            
            api.setToken(token);
            setUser(user);
            localStorage.setItem('auth_user', JSON.stringify(user));
            await setupPusherSubscriptions(user);
            
            return { success: true, user };
        } catch (error) {
            return { success: false, error: error.message };
        }
    };

    const register = async (userData) => {
        try {
            const response = await api.register(userData);
            const { user, token } = response.data;
            
            api.setToken(token);
            setUser(user);
            localStorage.setItem('auth_user', JSON.stringify(user));
            await setupPusherSubscriptions(user);
            
            return { success: true, user };
        } catch (error) {
            return { success: false, error: error.message };
        }
    };

    const logout = async () => {
        try {
            await api.logout();
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            setUser(null);
            api.removeToken();
            localStorage.removeItem('auth_user');
            await pusherService.clearAllSubscriptions();
        }
    };

    const updateProfile = async (profileData) => {
        try {
            const response = await api.updateProfile(profileData);
            setUser(response.data.user);
            localStorage.setItem('auth_user', JSON.stringify(response.data.user));
            return { success: true, user: response.data.user };
        } catch (error) {
            return { success: false, error: error.message };
        }
    };

    // Save current state before redirecting to login
    const saveStateAndRedirectToLogin = (currentState = {}, returnUrl = null) => {
        const stateToSave = {
            ...currentState,
            timestamp: Date.now(),
            returnUrl: returnUrl || window.location.pathname + window.location.search
        };
        
        localStorage.setItem('auth_pending_state', JSON.stringify(stateToSave));
        
        // Redirect to login page
        window.location.href = '/login';
    };

    // Restore saved state after successful login
    const restoreSavedState = () => {
        try {
            const savedState = localStorage.getItem('auth_pending_state');
            if (savedState) {
                const state = JSON.parse(savedState);
                localStorage.removeItem('auth_pending_state');
                
                // Check if state is not too old (24 hours)
                const isStateValid = Date.now() - state.timestamp < 24 * 60 * 60 * 1000;
                
                if (isStateValid) {
                    return state;
                }
            }
        } catch (error) {
            console.error('Error restoring saved state:', error);
            localStorage.removeItem('auth_pending_state');
        }
        return null;
    };

    // Check if user needs authentication for an action
    const requireAuth = (action, currentState = {}, returnUrl = null) => {
        if (!user) {
            saveStateAndRedirectToLogin(currentState, returnUrl);
            return false;
        }
        return true;
    };

    const value = {
        user,
        loading,
        login,
        register,
        logout,
        updateProfile,
        saveStateAndRedirectToLogin,
        restoreSavedState,
        requireAuth,
        isAuthenticated: !!user,
        isVendor: user?.user_type === 'vendor',
        isAdmin: user?.user_type === 'admin',
        isCustomer: user?.user_type === 'customer',
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
